import React, { memo, useCallback, useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useParams } from 'react-router';

import { Box, Button } from '@mui/material';
import { LoanApplicationTabId } from 'app/pages/loanApplication/LoanApplication.constants';
import { FormNames } from 'app/services/autoLockForms/autoLockForms.constants';
import { ButtonGroupWrapper } from 'app/shared/componentsRestyled/coreDesignSystem/Buttons';
import { DataFieldCDS, TextFieldCDS } from 'app/shared/componentsRestyled/coreDesignSystem/form';
import FormGeneratorDateFieldCDS from 'app/shared/componentsRestyled/coreDesignSystem/formGenerator/FormGeneratorDateFieldCDS';
import dayjs from 'dayjs';
import _ from 'lodash';
import PropTypes from 'prop-types';

import { COMMON_REQUEST_DATE_FORMAT, MAX_CALENDAR_DATE } from 'constants/date';
import { FieldInputType } from 'constants/fields';
import { DocumentExpirationDateType } from 'constants/loanApplication';
import { PendingChangesActionType } from 'constants/pendingChanges';
import { HORIZONTAL_ALIGNMENT } from 'constants/ui';
import useForm from 'hooks/useForm';
import { trimBodyStringValues } from 'utils/data';
import { parseCalendarDate } from 'utils/date';

import { selectFormLockedFields, selectFormLockedPage } from 'store/loanApplications/selectors';
import { getDocumentExpirationDatesAction } from 'store/underwriting/actions';
import {
  selectDocumentExpirationDatePendingChangesById,
  selectDocumentExpirationDatesBusyTitles,
} from 'store/underwriting/selectors';

import { UnderwritingTestIds } from '../../Underwriting.testIds';
import { TableCellId } from '../DocumentExpirationDates.constants';
import { checkIsDefaultDocumentExpirationDate } from '../DocumentExpirationDates.utils';

import { formConfig, formFields } from './DocumentExpirationDateForm.config';
import { wrapperStyles } from './DocumentExpirationDateForm.styles';
import {
  getIsFieldChangedMapByFieldName,
  getIsFieldDisabledByAutoLockMapByFieldName,
  getOnSaveAction,
  getSchema,
} from './DocumentExpirationDateForm.utils';

const DocumentExpirationDateForm = ({ dataToEdit, setDataToEdit }) => {
  const dispatch = useDispatch();

  const { applicationId } = useParams();

  const [isSaving, setIsSaving] = useState(false);

  const busyTitles = useSelector(selectDocumentExpirationDatesBusyTitles(dataToEdit.id));
  const initialPendingChanges = useSelector(
    selectDocumentExpirationDatePendingChangesById(dataToEdit.id),
  );
  const autoLockDisabledFields = useSelector(
    selectFormLockedFields(FormNames.UNDERWRITING_DOCUMENT_EXPIRATION_DATES),
  );
  const isPageLocked = useSelector(
    selectFormLockedPage(FormNames.UNDERWRITING_DOCUMENT_EXPIRATION_DATES),
  );

  const isDisabledByLock = isSaving || isPageLocked;

  const { id, documentExpirationDateType, pendingChangesActionType, isTempId } = dataToEdit;
  const isDefaultDocumentExpirationDate = checkIsDefaultDocumentExpirationDate(
    documentExpirationDateType,
  );
  const isDefaultDocumentExpirationDateToCreate = isDefaultDocumentExpirationDate && isTempId;
  const isNewRecord = !id;
  const isAdded = isNewRecord || pendingChangesActionType === PendingChangesActionType.ADDED;
  const isDeleted = pendingChangesActionType === PendingChangesActionType.DELETED;

  const initialFormValues = useMemo(
    () => ({
      ..._.pick(dataToEdit, formFields),
      [TableCellId.START_DATE]: parseCalendarDate(dataToEdit[TableCellId.START_DATE]),
      [TableCellId.EXPIRATION_DATE]: parseCalendarDate(dataToEdit[TableCellId.EXPIRATION_DATE]),
    }),
    [dataToEdit],
  );

  const {
    form,
    validationErrors,
    validateForm,
    validateField,
    handleChange,
    isFormChanged,
    setSchema,
    setInForm,
    formRef,
    staticValidateField,
    formPendingChanges,
  } = useForm(initialFormValues, formConfig, formFields);

  const startDate = form[TableCellId.START_DATE];
  const expiresInDays = form[TableCellId.EXPIRES_IN_DAYS];
  const maxExpiresInDays = startDate
    ? dayjs(MAX_CALENDAR_DATE).diff(dayjs(startDate), 'day')
    : null;

  const isFieldChangedMapByFieldName = useMemo(() => {
    if (isAdded) {
      return {};
    }

    return getIsFieldChangedMapByFieldName({
      initialPendingChanges,
      formPendingChanges,
    });
  }, [initialPendingChanges, formPendingChanges, isAdded]);

  const isFieldDisabledByAutoLockMapByFieldName = useMemo(
    () => getIsFieldDisabledByAutoLockMapByFieldName(autoLockDisabledFields),
    [autoLockDisabledFields],
  );

  const onCancel = useCallback(() => {
    setDataToEdit(null);
  }, [setDataToEdit]);

  const onSave = useCallback(async () => {
    if (!(await validateForm())) {
      return;
    }

    const form = formRef.current;

    const action = getOnSaveAction({ isNewRecord, isDefaultDocumentExpirationDateToCreate });

    const requestBody = trimBodyStringValues({
      applicationId,
      id,
      documentExpirationDateType: documentExpirationDateType ?? DocumentExpirationDateType.OTHER,
      [TableCellId.TITLE]: form[TableCellId.TITLE],
      [TableCellId.START_DATE]: dayjs(form[TableCellId.START_DATE]).format(
        COMMON_REQUEST_DATE_FORMAT,
      ),
      [TableCellId.EXPIRES_IN_DAYS]: Number(form[TableCellId.EXPIRES_IN_DAYS]),
      requestMetadata: {
        tabId: LoanApplicationTabId.UNDERWRITING,
      },
    });

    setIsSaving(true);

    dispatch(action(requestBody))
      .then(() => dispatch(getDocumentExpirationDatesAction({ applicationId })))
      .then(onCancel)
      .finally(() => setIsSaving(false));
  }, [
    dispatch,
    applicationId,
    validateForm,
    formRef,
    isNewRecord,
    id,
    onCancel,
    documentExpirationDateType,
    isDefaultDocumentExpirationDateToCreate,
  ]);

  useEffect(() => {
    setSchema(getSchema({ busyTitles }));
  }, [setSchema, busyTitles]);

  useEffect(() => {
    if (!isDeleted) {
      return;
    }

    onCancel();
  }, [onCancel, isDeleted]);

  useEffect(() => {
    if (!startDate || !expiresInDays) {
      return setInForm(TableCellId.EXPIRATION_DATE, null, false);
    }

    const isExpiresInDaysMoreThanMax = expiresInDays > maxExpiresInDays;

    if (isExpiresInDaysMoreThanMax) {
      setInForm(TableCellId.EXPIRES_IN_DAYS, maxExpiresInDays, false);
      staticValidateField(TableCellId.EXPIRES_IN_DAYS, maxExpiresInDays);
    }

    const expirationDate = dayjs(startDate).add(
      isExpiresInDaysMoreThanMax ? maxExpiresInDays : expiresInDays,
      'day',
    );

    setInForm(TableCellId.EXPIRATION_DATE, expirationDate, false);
  }, [startDate, expiresInDays, maxExpiresInDays, setInForm, staticValidateField]);

  return (
    <Box data-testid={UnderwritingTestIds.DOC_EXPIRATION_FORM} sx={wrapperStyles}>
      <TextFieldCDS
        required
        withPopupError
        dataTestId={UnderwritingTestIds.DOC_EXPIRATION_FORM_TITLE_INPUT}
        disabled={
          isDisabledByLock ||
          isDefaultDocumentExpirationDate ||
          isFieldDisabledByAutoLockMapByFieldName[TableCellId.TITLE]
        }
        error={!!validationErrors[TableCellId.TITLE]}
        helperText={validationErrors[TableCellId.TITLE]}
        isFieldChanged={isFieldChangedMapByFieldName[TableCellId.TITLE]}
        name={TableCellId.TITLE}
        value={form[TableCellId.TITLE]}
        onChange={handleChange}
      />

      <FormGeneratorDateFieldCDS
        required
        withPopupError
        disabled={
          isDisabledByLock || isFieldDisabledByAutoLockMapByFieldName[TableCellId.START_DATE]
        }
        error={!!validationErrors[TableCellId.START_DATE]}
        helperText={validationErrors[TableCellId.START_DATE]}
        isFieldChanged={isFieldChangedMapByFieldName[TableCellId.START_DATE]}
        label=""
        name={TableCellId.START_DATE}
        setInForm={setInForm}
        validateField={validateField}
        value={form[TableCellId.START_DATE]}
        onChange={handleChange}
      />

      <DataFieldCDS
        required
        withPopupError
        disabled={
          isDisabledByLock || isFieldDisabledByAutoLockMapByFieldName[TableCellId.EXPIRES_IN_DAYS]
        }
        error={!!validationErrors[TableCellId.EXPIRES_IN_DAYS]}
        helperText={validationErrors[TableCellId.EXPIRES_IN_DAYS]}
        isFieldChanged={isFieldChangedMapByFieldName[TableCellId.EXPIRES_IN_DAYS]}
        maxValue={maxExpiresInDays}
        name={TableCellId.EXPIRES_IN_DAYS}
        type={FieldInputType.INT}
        value={form[TableCellId.EXPIRES_IN_DAYS]}
        onChange={handleChange}
      />

      <FormGeneratorDateFieldCDS
        disabled
        required
        withPopupError
        error={!!validationErrors[TableCellId.EXPIRATION_DATE]}
        helperText={validationErrors[TableCellId.EXPIRATION_DATE]}
        isFieldChanged={isFieldChangedMapByFieldName[TableCellId.EXPIRATION_DATE]}
        label=""
        name={TableCellId.EXPIRATION_DATE}
        setInForm={setInForm}
        validateField={validateField}
        value={form[TableCellId.EXPIRATION_DATE]}
        onChange={handleChange}
      />

      <ButtonGroupWrapper alignment={HORIZONTAL_ALIGNMENT.CENTER}>
        <Button
          data-testid={UnderwritingTestIds.DOC_EXPIRATION_FORM_CANCEL_BUTTON}
          disabled={isSaving}
          size="small"
          variant="outlined"
          onClick={onCancel}
        >
          Cancel
        </Button>
        <Button
          data-testid={UnderwritingTestIds.DOC_EXPIRATION_FORM_SAVE_BUTTON}
          disabled={!isFormChanged || isSaving}
          size="small"
          onClick={onSave}
        >
          Save
        </Button>
      </ButtonGroupWrapper>
    </Box>
  );
};

DocumentExpirationDateForm.propTypes = {
  dataToEdit: PropTypes.object.isRequired,
  setDataToEdit: PropTypes.func.isRequired,
};

export default memo(DocumentExpirationDateForm);
